import Logo from '@/assets/logo.svg';
import { Icon } from '@/components/ui/Icon';
import { usePermissions } from '@/hooks/usePermissions';
import { Action, Subject } from '@/lib/permissions';
import { useUI } from '@/lib/uiContext';
import {
  mdiAccount,
  mdiChartBar,
  mdiClose,
  mdiFilter,
  mdiImport,
  mdiMonitor,
  mdiStore,
  mdiTag,
} from '@mdi/js';
import cx from 'classnames';
import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';

interface MenuItem {
  label: string;
  href: string;
  icon: string;
  requiredPermission?: { action: Action; subject: Subject };
}

const menuItems: MenuItem[] = [
  {
    label: 'Dashboard',
    href: '/dashboard',
    icon: mdiMonitor,
  },
  {
    label: 'Analytics',
    href: '/analytics',
    icon: mdiChartBar,
    requiredPermission: { action: 'read', subject: 'Analytics' },
  },
  {
    label: 'Users',
    href: '/users',
    icon: mdiAccount,
    requiredPermission: { action: 'read', subject: 'User' },
  },
  {
    label: 'Businesses',
    href: '/businesses',
    icon: mdiStore,
    requiredPermission: { action: 'read', subject: 'Business' },
  },
  {
    label: 'Import',
    href: '/import',
    icon: mdiImport,
    requiredPermission: { action: 'create', subject: 'Business' },
  },
  {
    label: 'Categories',
    href: '/categories',
    icon: mdiTag,
    requiredPermission: { action: 'read', subject: 'Category' },
  },
  {
    label: 'Filters',
    href: '/filters',
    icon: mdiFilter,
    requiredPermission: { action: 'read', subject: 'Filter' },
  },
];

export const Sidebar: React.FC = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const { sidebarOpen, toggleSidebar } = useUI();
  const { can } = usePermissions();

  const handleClose = () => {
    toggleSidebar();
  };

  const isActive = (href: string) => {
    return (
      pathname === href || (href !== '/dashboard' && pathname.startsWith(href))
    );
  };

  const canAccess = (item: MenuItem) => {
    if (!item.requiredPermission) return true;
    return can(item.requiredPermission.action, item.requiredPermission.subject);
  };

  return (
    <>
      {/* Sidebar */}
      <aside
        className={cx(
          'w-64 h-screen bg-white border-r border-gray-200 fixed top-0 left-0 z-10 transform transition-transform duration-300 ease-in-out lg:transform-none',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {/* Header */}
        <div className='flex items-center justify-between p-6 border-b border-gray-200'>
          <div className='flex items-center space-x-2'>
            <img src={Logo} alt='Coffice Admin' className='h-8' />
          </div>
          <button
            onClick={handleClose}
            className='lg:hidden inline-flex items-center justify-center p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 rounded-sm transition-colors'
          >
            <Icon path={mdiClose} size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className='p-4'>
          <ul className='space-y-1'>
            {menuItems.filter(canAccess).map(item => (
              <li key={item.href}>
                <Link
                  to={item.href}
                  className={cx(
                    'flex items-center px-4 py-2 text-sm font-medium rounded-sm transition-colors',
                    isActive(item.href)
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                  )}
                  onClick={() => {
                    // Close sidebar on mobile when clicking a link
                    if (window.innerWidth < 1024) {
                      handleClose();
                    }
                  }}
                >
                  <Icon
                    path={item.icon}
                    size={18}
                    className={cx(
                      'mr-2 transition-opacity',
                      isActive(item.href) ? 'opacity-100' : 'opacity-70',
                    )}
                  />
                  <span>{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </aside>
    </>
  );
};
